import { useId, useState } from "react";
import type { EarlierConversation } from "../../../skills/leadcue-reply/scripts/lib/earlier-conversation.ts";
import { appDateTime } from "../src/app-dates.ts";
import {
  EARLIER_EXPLANATION,
  earlierAuthor,
  earlierCampaignName,
  earlierOmitted,
  earlierSpan,
} from "../src/earlier-conversation-view.ts";
import { MoreIcon } from "./icons.tsx";

/**
 * A reply to a follow-up campaign: the lead's conversation in the earlier campaign, folded at the
 * head of the thread because it came first. Marked as another campaign's before anything else, so
 * the rep never reads its offer as this one's. Shown as the plain text drafting read.
 */
export function EarlierConversationFold({ conversation }: { conversation: EarlierConversation }) {
  const [open, setOpen] = useState(false);
  const regionId = useId();
  const omitted = earlierOmitted(conversation);
  return (
    <div className="lcr-earlier-campaign">
      <button
        type="button"
        className="lcr-quote-toggle lcr-earlier-campaign-toggle"
        aria-expanded={open}
        aria-controls={regionId}
        onClick={() => setOpen(!open)}
      >
        <MoreIcon size={16} />
        <span className="lcr-pill lcr-pill-amber">Earlier campaign</span>
        <span className="lcr-earlier-campaign-name">{earlierCampaignName(conversation)}</span>
        <span className="lcr-quote-toggle-note">{earlierSpan(conversation)}</span>
      </button>
      {open && (
        <section
          id={regionId}
          className="lcr-earlier-campaign-body"
          aria-label="Earlier campaign conversation"
        >
          <p className="lcr-earlier-campaign-explain">{EARLIER_EXPLANATION}</p>
          {omitted && <p className="lcr-earlier-campaign-omitted">{omitted}</p>}
          <ol className="lcr-earlier-campaign-list">
            {conversation.messages.map((message) => (
              <li
                key={message.id}
                className="lcr-earlier-campaign-message"
                data-from={message.from}
              >
                <div className="lcr-mail-head">
                  <strong>{earlierAuthor(message.from)}</strong>
                  <time dateTime={message.date_received}>{appDateTime(message.date_received)}</time>
                </div>
                {message.subject && (
                  <p className="lcr-earlier-campaign-subject">{message.subject}</p>
                )}
                <p className="lcr-earlier-campaign-text">
                  {message.truncated ? `${message.text}…` : message.text}
                </p>
                {message.truncated && (
                  <p className="lcr-earlier-campaign-cut">
                    Shortened: the draft read the first part of this message.
                  </p>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}
